import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Grid } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { Button } from '#root/components/ui';
import { format } from 'date-fns';

import { validateURL, parseYmd } from '#root/utils';
import { useNotification } from '#root/hooks';
import { tripService, travelerService } from '#root/services';
import { GuveryPageHeader, GuveryLayout, GuverySpace } from '#root/ui-components';
import { TRAVELER_DEALS_CREATED } from '#root/constants/navigation';
import styles from './travelerDealAuto.module.scss';
import DealShopper from './DealShopper';
import DealOrders from './DealOrders';
import DealDelivery from './DealDelivery';

type baseDataDTO = {
  shopper: any;
  tripId: string;
  packaging_type: string;
  delivery_date: Date | string;
  zipcode: string;
  delivery_place: string;
  expiration_date: Date | string;
  shipping: boolean;
  title: string;
  observation: string;
  deal_orders: any;
};

const baseData: baseDataDTO = {
  shopper: null,
  tripId: '',
  packaging_type: '',
  delivery_date: new Date(),
  zipcode: '',
  delivery_place: '',
  expiration_date: new Date(),
  shipping: false,
  title: '',
  observation: '',
  deal_orders: [
    {
      url: '',
      can: 1,
      pre: 0,
      com: 0,
    },
  ],
};

const reducerQty = (accumulator, current) => Number(accumulator) + Number(current?.can);
const reducerPrice = (accumulator, current) => Number(accumulator) + Number(current?.pre);
const reducerCom = (accumulator, current) => Number(accumulator) + Number(current?.com);

const TravelerDealAuto = () => {
  const history = useHistory();
  const { createMessage } = useNotification();
  const [sendDisabled, setSendDisabled] = useState(false);
  const [dealForm, setDealForm] = useState(baseData);
  const [trips, setTrips] = useState([]);
  const [tripsLoaded, setTripsLoaded] = useState(false);

  const qtyTotal = dealForm?.deal_orders?.reduce(reducerQty, 0);
  const priceTotal = dealForm?.deal_orders?.reduce(reducerPrice, 0);
  const comTotal = dealForm?.deal_orders?.reduce(reducerCom, 0);

  const goBack = () => history.push(TRAVELER_DEALS_CREATED);

  const handleValidate = () => {
    // Validaciones declarativas: si alguna condición es verdadera, el formulario es inválido
    const validationRules = [
      // URLs válidas en pedidos
      () => dealForm?.deal_orders?.some((order) => !validateURL(order?.url)),
      // Campos obligatorios
      () => !dealForm?.shopper,
      () => !dealForm?.tripId,
      () => !dealForm?.title,
      () => !dealForm?.delivery_date,
      () => !dealForm?.delivery_place,
      () => !dealForm?.expiration_date,
      () => !dealForm?.packaging_type,
      // Totales mayores a cero
      () => qtyTotal <= 0,
      () => priceTotal <= 0,
      () => comTotal <= 0,
    ];

    // Si alguna regla falla, deshabilitar submit
    const isInvalid = validationRules.some((rule) => rule());
    setSendDisabled(isInvalid);
  };

  const handleSend = () => {
    fetchNewAutoDeal();
  };

  const fetchGetCurrentTrip = async () => {
    const res = await tripService.getCurrent();
    // `/trips/current` responde HTTP 200 aun cuando falla (response_failed): el
    // viaje real se reconoce por su `id`.
    const currentTrip = !res.info.error && res.data?.id ? res.data : null;
    // Sin viaje vigente no se prellena nada, pero tampoco se bloquea la vista: el
    // viajero puede elegir otro viaje en el selector de "Detalles de la entrega".
    if (!currentTrip || currentTrip.is_offerable !== true) {
      return;
    }
    setDealForm({
      ...dealForm,
      tripId: currentTrip.id,
      delivery_date: parseYmd(currentTrip.fecha_entrega) ?? new Date(),
      expiration_date: parseYmd(currentTrip.offer_expiration_date) ?? new Date(),
      delivery_place: currentTrip.lugar_entrega,
      zipcode: currentTrip.zipcode,
    });
  };

  const fetchTrips = async () => {
    const res = await tripService.list();
    if (!res.error) {
      // Solo viajes con los que el server permite ofertar hoy.
      const newOrderArray = [
        ...res.data?.filter((item) => item.estado === 'CREADO' && item.is_offerable === true),
      ];
      setTrips(newOrderArray);
    }
    setTripsLoaded(true);
  };

  const fetchNewAutoDeal = async () => {
    setSendDisabled(true);
    createMessage.info('Creando el nuevo acuerdo...', 'create-auto-deal');
    const arg = {
      shopper_idu: dealForm.shopper?.idu,
      delivery_date: format(new Date(dealForm.delivery_date), 'yyyy-MM-dd'),
      delivery_place: dealForm.delivery_place,
      observation: dealForm.observation,
      title: dealForm.title,
      flag_province: dealForm.shipping ? 1 : 0,
      packing: dealForm.packaging_type,
      trip_id: dealForm.tripId,
      expiration_date: format(new Date(dealForm.expiration_date), 'yyyy-MM-dd'),
      deal_orders: dealForm.deal_orders,
    };
    const res = await travelerService.dealAuto(arg);
    if (!res.error) {
      createMessage.success('Auto Acuerdo creado', 'create-auto-deal');
      history.push(TRAVELER_DEALS_CREATED);
      return;
    }
    createMessage.error('No se pudo crear el Auto Acuerdo', 'create-auto-deal');
    setSendDisabled(false);
  };

  useEffect(() => {
    fetchGetCurrentTrip();
    fetchTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleValidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qtyTotal, priceTotal, comTotal, dealForm]);

  const title = (
    <span className={styles.title}>
      <span>Creando Auto Acuerdo</span>
    </span>
  );

  return (
    <GuveryLayout>
      <GuveryPageHeader backButton title={title} onBack={goBack} />
      <Grid container spacing={3}>
        {tripsLoaded && trips.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="error">
              Tu viaje seleccionado ya no está vigente para ofertar (fecha de viaje
              vencida). Selecciona o crea un viaje con fecha de viaje igual o posterior a
              hoy.
            </Alert>
          </Grid>
        )}
        <Grid item xs={12} md={8}>
          <GuverySpace direction="vertical" size="large">
            <DealShopper dealForm={dealForm} setDealForm={setDealForm} />
            <DealOrders
              dealForm={dealForm}
              setDealForm={setDealForm}
              setSendDisabled={setSendDisabled}
            />
          </GuverySpace>
        </Grid>
        <Grid item xs={12} md={4}>
          <GuverySpace direction="vertical" size="large">
            <DealDelivery dealForm={dealForm} setDealForm={setDealForm} trips={trips} />
            <Button
              fullWidth
              disabled={sendDisabled}
              variant="primary"
              onClick={handleSend}>
              Guardar y Enviar Acuerdo
            </Button>
          </GuverySpace>
        </Grid>
      </Grid>
    </GuveryLayout>
  );
};

export default TravelerDealAuto;
